import { Badge, Button, Card, Spinner } from '@axioma/ui';
import { useQuery } from '@tanstack/react-query';
import { FileSearch } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { AutosViewer } from '../components/AutosViewer.js';
import { PageHeader } from '../components/PageHeader.js';
import { api } from '../lib/api.js';

interface MatterDetailResponse {
  matter: {
    id: string;
    matterNumber: string;
    title: string;
    description: string | null;
    area: string;
    status: string;
    confidential: boolean;
    causeValueCents: string | null;
    client: { id: string; name: string };
    parties: Array<{ id: string; name: string; partyRole: string; partyType: string }>;
    judicialProcesses: Array<{ id: string; cnjNumberMasked: string; court: string }>;
  };
}

export function MatterDetail(): JSX.Element {
  const { id } = useParams();
  const [autosOpen, setAutosOpen] = useState<{ cnj: string; cnjMasked: string } | null>(null);
  const query = useQuery({
    queryKey: ['matter', id],
    queryFn: async () => {
      const { data } = await api.get<MatterDetailResponse>(`/matters/${String(id)}`);
      return data;
    },
    enabled: Boolean(id),
  });

  if (query.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" variant="aurora" />
      </div>
    );
  }
  if (!query.data) {
    return (
      <div className="px-8 py-10 font-mono uppercase text-ax-text-tertiary">
        Matter não encontrado.
      </div>
    );
  }
  const m = query.data.matter;

  return (
    <div>
      <PageHeader
        eyebrow={`Matter · ${m.matterNumber}`}
        title={m.title}
        description={
          <>
            Cliente: <strong className="font-medium text-ax-text-primary">{m.client.name}</strong>
            {m.confidential && (
              <Badge variant="critical" className="ml-3">
                Sigiloso
              </Badge>
            )}
          </>
        }
      />
      <div className="grid grid-cols-1 gap-5 px-8 py-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <div className="px-5 py-4">
              <h2 className="ax-eyebrow mb-2 text-ax-text-tertiary">Descrição</h2>
              <p className="ax-body whitespace-pre-wrap text-ax-text-secondary">
                {m.description ?? 'Sem descrição.'}
              </p>
            </div>
          </Card>

          <Card>
            <div className="px-5 py-4">
              <h2 className="ax-eyebrow mb-3 text-ax-text-tertiary">Processos vinculados</h2>
              {m.judicialProcesses.length === 0 ? (
                <p className="ax-body-sm text-ax-text-tertiary">Nenhum processo vinculado.</p>
              ) : (
                <ul className="space-y-2">
                  {m.judicialProcesses.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 border-b border-ax-line py-1.5 last:border-b-0"
                    >
                      <span className="font-mono text-sm text-ax-text-primary">
                        {p.cnjNumberMasked}
                      </span>
                      <span className="font-mono text-[11px] uppercase text-ax-text-tertiary">
                        {p.court}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<FileSearch size={12} />}
                        className="ml-auto"
                        onClick={() =>
                          setAutosOpen({
                            cnj: p.cnjNumberMasked.replace(/\D/g, ''),
                            cnjMasked: p.cnjNumberMasked,
                          })
                        }
                      >
                        Abrir autos
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <div className="space-y-3 px-5 py-4">
              <div>
                <p className="ax-eyebrow text-ax-text-tertiary">Status</p>
                <p className="mt-1 font-display text-sm text-ax-text-primary">{m.status}</p>
              </div>
              <div>
                <p className="ax-eyebrow text-ax-text-tertiary">Área</p>
                <p className="mt-1 font-display text-sm text-ax-text-primary">{m.area}</p>
              </div>
              {m.causeValueCents && (
                <div>
                  <p className="ax-eyebrow text-ax-text-tertiary">Valor da causa</p>
                  <p className="mt-1 font-mono text-sm text-ax-text-primary">
                    {(Number(m.causeValueCents) / 100).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                  </p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div className="px-5 py-4">
              <h2 className="ax-eyebrow mb-3 text-ax-text-tertiary">Partes ({m.parties.length})</h2>
              <ul className="space-y-2">
                {m.parties.map((p) => (
                  <li key={p.id}>
                    <p className="font-display text-sm leading-tight text-ax-text-primary">
                      {p.name}
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-ax-text-tertiary">
                      {p.partyRole} · {p.partyType}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      </div>
      {autosOpen && (
        <AutosViewer
          cnj={autosOpen.cnj}
          cnjMasked={autosOpen.cnjMasked}
          onClose={() => setAutosOpen(null)}
        />
      )}
    </div>
  );
}
